import React, { useState, useEffect } from 'react';
import { ArrowLeft, TrendingUp, TrendingDown, Clock, Shield, BarChart3, ChevronDown, ExternalLink } from 'lucide-react';
import { BACKEND_URL } from '../../services/config';
import ConfidenceRing from './ConfidenceRing';
import PairSelector from './PairSelector';

/**
 * TradeEngine — Execution Screen
 * Shows full signal details + chart + execute in MT5
 */
const TradeEngine = ({ onBack }) => {
  const [pair, setPair] = useState(() => localStorage.getItem('mbe_selected_pair') || 'XAU/USD');
  const [signal, setSignal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [timeframe, setTimeframe] = useState('M5');
  const [showTfDropdown, setShowTfDropdown] = useState(false);

  const timeframes = ['M5', 'M15', 'M30', 'H1', 'H4'];

  useEffect(() => { localStorage.setItem('mbe_selected_pair', pair); }, [pair]);

  useEffect(() => {
    fetchSignal();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeframe, pair]);

  const [marketClosedMsg, setMarketClosedMsg] = useState('');
  const [noTradeInfo, setNoTradeInfo] = useState(null);

  const fetchSignal = async () => {
    setLoading(true);
    setMarketClosedMsg('');
    setNoTradeInfo(null);
    try {
      const token = localStorage.getItem('fira_auth_token');
      const res = await fetch(`${BACKEND_URL}/api/premium/signals/generate?language=en`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ pair, timeframe }),
      });
      const data = await res.json();
      const errMsg = data.detail || data.error || '';
      if (res.status === 400 && /market.*closed/i.test(errMsg)) {
        setSignal(null);
        setMarketClosedMsg(errMsg);
      } else if (data.signal) {
        const d = data.signal.direction;
        if (d === 'NEUTRAL' || d === 'NO TRADE' || d === 'TIMEOUT') {
          setSignal(null);
          setNoTradeInfo({
            reasons: data.signal.no_trade_reasons || [data.signal.analysis].filter(Boolean),
            current_price: data.signal.current_price,
          });
        } else {
          setSignal(data.signal);
        }
      } else {
        setSignal(null);
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  };

  const isBuy = signal?.direction === 'BUY';
  const entry = signal?.entry_zone || {};
  const entryFrom = entry.from || 0;
  const sl = signal?.stop_loss || 0;
  const tp = signal?.take_profit || [];
  const tp1 = Array.isArray(tp) ? tp[0] : tp?.tp1;
  const tp2 = Array.isArray(tp) ? tp[1] : tp?.tp2;
  const tp3 = Array.isArray(tp) ? tp[2] : tp?.tp3;

  return (
    <div data-testid="trade-engine" style={{ fontFamily: 'Inter, sans-serif', maxWidth: '480px', margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '20px' }}>
        <button data-testid="trade-back-btn" onClick={onBack} style={{ background: '#F1F5F9', border: 'none', borderRadius: '12px', padding: '10px', cursor: 'pointer', display: 'flex' }}>
          <ArrowLeft size={20} color="#64748B" />
        </button>
        <div style={{ flex: 1 }}>
          <h1 style={{ fontSize: '20px', fontWeight: '700', color: '#0F172A', margin: 0, fontFamily: 'Poppins, sans-serif' }}>Trade Engine</h1>
          <span style={{ fontSize: '12px', color: '#94A3B8' }}>{pair} Execution</span>
        </div>
        {/* Pair + Timeframe Selectors */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <PairSelector value={pair} onChange={setPair} compact />
          <div style={{ position: 'relative' }}>
            <button onClick={() => setShowTfDropdown(!showTfDropdown)} style={{
              display: 'flex', alignItems: 'center', gap: '6px', padding: '6px 10px', borderRadius: '12px',
              border: '1px solid #E2E8F0', background: '#FFF', fontSize: '12px', fontWeight: '600', color: '#0F172A', cursor: 'pointer',
            }}>
              {timeframe} <ChevronDown size={14} />
            </button>
            {showTfDropdown && (
              <div style={{ position: 'absolute', top: '100%', right: 0, marginTop: '4px', background: '#FFF', borderRadius: '12px', border: '1px solid #E2E8F0', boxShadow: '0 8px 30px rgba(0,0,0,0.08)', zIndex: 10, overflow: 'hidden' }}>
                {timeframes.map((tf) => (
                  <button key={tf} onClick={() => { setTimeframe(tf); setShowTfDropdown(false); }} style={{
                    display: 'block', width: '100%', padding: '10px 20px', border: 'none', background: tf === timeframe ? '#FFF7ED' : '#FFF',
                    color: tf === timeframe ? '#F5A623' : '#334155', fontSize: '13px', fontWeight: '600', cursor: 'pointer', textAlign: 'left',
                  }}>{tf}</button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {loading ? (
        <div style={{ background: '#FFF', borderRadius: '24px', padding: '48px', textAlign: 'center', border: '1px solid #E2E8F0' }}>
          <div style={{ width: '40px', height: '40px', borderRadius: '50%', border: '3px solid #F1F5F9', borderTopColor: '#F5A623', animation: 'spin 0.8s linear infinite', margin: '0 auto 16px' }} />
          <p style={{ color: '#94A3B8', fontSize: '14px' }}>Generating {timeframe} signal...</p>
        </div>
      ) : marketClosedMsg ? (
        <div data-testid="trade-market-closed" style={{ background: 'linear-gradient(180deg,#F8FAFC,#FFF)', borderRadius: '24px', padding: '36px 24px', textAlign: 'center', border: '1px solid #E2E8F0' }}>
          <div style={{ width: '52px', height: '52px', borderRadius: '50%', background: 'rgba(100,116,139,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px' }}>
            <Clock size={24} color="#64748B" />
          </div>
          <h3 style={{ fontSize: '17px', fontWeight: 700, color: '#0F172A', margin: '0 0 6px 0', fontFamily: 'Poppins, sans-serif' }}>Market Closed</h3>
          <p style={{ fontSize: '12px', color: '#64748B', lineHeight: 1.5, margin: '0 auto', maxWidth: '340px' }}>
            {pair} trades during forex sessions (Monday–Friday). Try BTC/USD or ETH/USD for 24/7 action.
          </p>
        </div>
      ) : noTradeInfo ? (
        <div data-testid="trade-no-setup" style={{ background: 'linear-gradient(180deg,#FFFBF2,#FFF)', borderRadius: '24px', padding: '30px 22px', textAlign: 'center', border: '1px solid rgba(245,166,35,0.25)' }}>
          <div style={{ width: '52px', height: '52px', borderRadius: '50%', background: 'rgba(245,166,35,0.10)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 14px' }}>
            <Clock size={24} color="#F5A623" />
          </div>
          <h3 style={{ fontSize: '17px', fontWeight: 700, color: '#0F172A', margin: '0 0 6px 0', fontFamily: 'Poppins, sans-serif' }}>No High-Confidence Setup</h3>
          <p style={{ fontSize: '12px', color: '#64748B', lineHeight: 1.5, margin: '0 auto 10px', maxWidth: '340px' }}>
            Market is open but our AI hasn't found a strong {pair} {timeframe} opportunity right now.
          </p>
          {noTradeInfo.current_price && (
            <div style={{ display: 'inline-block', padding: '6px 14px', borderRadius: '999px', background: '#F8FAFC', border: '1px solid #E2E8F0', fontSize: '12px', fontWeight: 600, color: '#0F172A' }}>
              {pair}: <span style={{ color: '#F5A623' }}>${Number(noTradeInfo.current_price).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
            </div>
          )}
          {Array.isArray(noTradeInfo.reasons) && noTradeInfo.reasons.length > 0 && (
            <div style={{ marginTop: '10px', padding: '10px 12px', background: '#FFF', borderRadius: '12px', border: '1px solid #F1F5F9', fontSize: '11px', color: '#64748B', textAlign: 'left', lineHeight: 1.5 }}>
              <strong style={{ color: '#475569', fontWeight: 700, display: 'block', marginBottom: '4px' }}>Why no trade?</strong>
              {noTradeInfo.reasons.slice(0, 2).map((r, i) => <div key={i}>• {r}</div>)}
            </div>
          )}
          <div style={{ marginTop: '12px', fontSize: '11px', color: '#94A3B8' }}>
            Try another timeframe — M5, M15, M30, H1, H4
          </div>
        </div>
      ) : signal && signal.direction !== 'NEUTRAL' && signal.direction !== 'NO TRADE' && signal.direction !== 'TIMEOUT' ? (
        <>
          {/* Direction Banner */}
          <div style={{
            background: isBuy ? 'linear-gradient(135deg, #10B981, #059669)' : 'linear-gradient(135deg, #EF4444, #DC2626)',
            borderRadius: '20px', padding: '20px 24px', marginBottom: '16px', color: '#FFF',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center'
          }}>
            <div>
              <div style={{ fontSize: '12px', opacity: 0.8, fontWeight: '600', marginBottom: '4px' }}>SIGNAL DIRECTION</div>
              <div style={{ fontSize: '28px', fontWeight: '700', fontFamily: 'Poppins, sans-serif', display: 'flex', alignItems: 'center', gap: '8px' }}>
                {isBuy ? <TrendingUp size={28} /> : <TrendingDown size={28} />}
                {signal.direction}
              </div>
            </div>
            <ConfidenceRing percentage={signal.confidence || 0} size={80} strokeWidth={7} />
          </div>

          {/* Trade Parameters Card */}
          <div style={{ background: '#FFF', borderRadius: '20px', padding: '20px', border: '1px solid #E2E8F0', marginBottom: '16px' }}>
            <div style={{ fontSize: '11px', fontWeight: '700', color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.12em', marginBottom: '14px' }}>Trade Parameters</div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              {[
                { label: 'Entry', value: entryFrom ? `$${Number(entryFrom).toFixed(2)}` : 'N/A', color: '#0F172A' },
                { label: 'Stop Loss', value: sl ? `$${Number(sl).toFixed(2)}` : 'N/A', color: '#EF4444' },
                { label: 'TP1', value: tp1 ? `$${Number(tp1).toFixed(2)}` : 'N/A', color: '#10B981' },
                { label: 'TP2', value: tp2 ? `$${Number(tp2).toFixed(2)}` : 'N/A', color: '#10B981' },
                { label: 'TP3', value: tp3 ? `$${Number(tp3).toFixed(2)}` : 'N/A', color: '#10B981' },
                { label: 'R:R', value: signal.risk_reward || '1:1.5', color: '#F5A623' },
              ].map((p, i) => (
                <div key={i} style={{ padding: '12px', background: '#F8FAFC', borderRadius: '14px' }}>
                  <div style={{ fontSize: '10px', fontWeight: '700', color: '#94A3B8', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: '4px' }}>{p.label}</div>
                  <div style={{ fontSize: '16px', fontWeight: '700', color: p.color, fontFamily: 'Inter' }}>{p.value}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Risk & Time */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '16px' }}>
            <div style={{ background: '#FFF', borderRadius: '16px', padding: '16px', border: '1px solid #E2E8F0' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                <Shield size={16} color="#F5A623" />
                <span style={{ fontSize: '11px', fontWeight: '700', color: '#94A3B8', textTransform: 'uppercase' }}>Risk Level</span>
              </div>
              <div style={{ fontSize: '16px', fontWeight: '700', color: '#0F172A' }}>{signal.confidence >= 80 ? 'Low' : signal.confidence >= 60 ? 'Medium' : 'High'}</div>
            </div>
            <div style={{ background: '#FFF', borderRadius: '16px', padding: '16px', border: '1px solid #E2E8F0' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                <Clock size={16} color="#F5A623" />
                <span style={{ fontSize: '11px', fontWeight: '700', color: '#94A3B8', textTransform: 'uppercase' }}>Valid For</span>
              </div>
              <div style={{ fontSize: '16px', fontWeight: '700', color: '#0F172A' }}>{signal.valid_hours || 2}h</div>
            </div>
          </div>

          {/* Execute Button */}
          <button data-testid="trade-execute-btn" style={{
            width: '100%', padding: '18px', borderRadius: '16px', border: 'none', cursor: 'pointer',
            background: isBuy ? 'linear-gradient(135deg, #10B981, #059669)' : 'linear-gradient(135deg, #EF4444, #DC2626)',
            color: '#FFF', fontSize: '17px', fontWeight: '700', fontFamily: 'Poppins', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px',
            boxShadow: isBuy ? '0 8px 24px rgba(16,185,129,0.3)' : '0 8px 24px rgba(239,68,68,0.3)',
          }}>
            <ExternalLink size={20} /> Execute in MT5
          </button>
        </>
      ) : (
        <div style={{ background: '#FFF', borderRadius: '24px', padding: '40px', textAlign: 'center', border: '1px solid #E2E8F0' }}>
          <BarChart3 size={40} color="#CBD5E1" style={{ marginBottom: '12px' }} />
          <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#475569', marginBottom: '6px' }}>No Signal Available</h3>
          <p style={{ fontSize: '13px', color: '#94A3B8' }}>Market conditions don't meet confidence threshold for {timeframe}. Try another timeframe.</p>
        </div>
      )}
    </div>
  );
};

export default TradeEngine;
